/**
 * Utility functions for Activity tracking components
 */

/**
 * Month name to number mapping for date parsing
 */
const MONTH_MAP: { [key: string]: number } = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/**
 * Get the label from a week object
 */
export const getWeekLabel = (week: any): string => {
  if (typeof week === "string") {
    return week; // Test format
  }
  return week.label || week; // Real format
};

/**
 * Parse date from various formats
 * Supports:
 * - DD-Mon-YY format (e.g., "06-Jan-25")
 * - MM/DD-MM/DD format (e.g., "01/01-01/07")
 */
export const parseDateLabel = (dateStr: string): Date => {
  if (dateStr.includes("/")) {
    // Test format: "01/01-01/07"
    const [startDate] = dateStr.split("-");
    const [month, day] = startDate.split("/");
    const currentYear = new Date().getFullYear();
    return new Date(currentYear, parseInt(month) - 1, parseInt(day));
  } else {
    // Real format: "06-Jan-25"
    const [day, month, year] = dateStr.split("-");
    const yearNum = parseInt(year);
    // Handle 2-digit years by determining the century
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const fullYear = yearNum < 100 ? currentCentury + yearNum : yearNum;
    
    return new Date(fullYear, MONTH_MAP[month], parseInt(day));
  }
};

/**
 * Sort weeks array by date chronologically
 */
export const sortWeeksByDate = (weeks: any[]): any[] => {
  return [...weeks].sort((a, b) => {
    const labelA = getWeekLabel(a);
    const labelB = getWeekLabel(b);
    const dateA = parseDateLabel(labelA);
    const dateB = parseDateLabel(labelB);
    return dateA.getTime() - dateB.getTime();
  });
};

/**
 * Calculate the maximum scroll position for synchronized scrolling
 */
export const calculateMaxScroll = (
  weeklyGrid: any,
  showTestFBOs: boolean,
  containerRef: HTMLDivElement | null,
  metricRowsCount: number,
  rowHeight: number
): number => {
  if (!containerRef || !weeklyGrid) return 0;
  
  const fboRowsCount = weeklyGrid.fbos?.filter(
    (fbo: any) => showTestFBOs || !fbo.is_test
  ).length || 0;
  
  const totalRows = metricRowsCount + fboRowsCount;
  const totalHeight = totalRows * rowHeight;
  const visibleHeight = containerRef.clientHeight;
  
  return Math.max(0, totalHeight - visibleHeight);
};

/**
 * Get stable ID for activity row
 */
export const getActivityRowId = (activity: any, index: number): string | number => {
  // Prefer existing ID, then timestamp, then fall back to index
  if (activity.id) return activity.id;
  if (activity.timestamp) return activity.timestamp;
  if (activity.user_email && activity.fbo_name && activity.timestamp) {
    // Create composite ID from unique fields
    return `${activity.user_email}-${activity.fbo_name}-${activity.timestamp}`;
  }
  return `activity-${index}`;
};